import { service } from "@ember/service";
import ForcJournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import dayjs from "dayjs";
import type { ValidatorMap } from "ember-changeset/types";
import type { IntlService } from "ember-intl";
import type { das28Je } from "parog-web/components/das28/utils/get-all-das28-jes";
import getAllDas28Jes from "parog-web/components/das28/utils/get-all-das28-jes";
import mostRecentJe from "parog-web/components/das28/utils/most-recent-je";
import { abstractField } from "parog-web/utils/decorators";
import { sortBy } from "parog-web/utils/sort";

interface ArticulationsInfos {
  nad: number | undefined;
  nag: number | undefined;
  occurredAt: Date | undefined;
}

interface FeelingsInfo {
  patientFeeling: number;
  occurredAt: Date | undefined;
}

/**
 * This component aims to refactor as much logic about DAS28 features as possible.
 * We may have to implement multiple components which rely on the same feature
 * and logic, but only a few details change (properties, how to compute the DAS, names...)
 *
 * TODO : to delete with Rhumatowise
 */
export default class Das28EditionFormComponent extends ForcJournalEventBasedComponent {
  @service declare intl: IntlService;

  /**
   * ABSTRACT
   */

  /**
   * The function that will be used to compute the DAS28
   * @param {Object} obj
   * @returns {number}
   */
  // @ts-expect-error ignored as Rhumatowise related code will disapear soon
  @abstractField
  currentComputeDas28Function;

  /**
   * The function that will be used to compute the DAS28 activity
   * @param {string} das28
   * @returns {string}
   */
  // @ts-expect-error ignored as Rhumatowise related code will disapear soon
  @abstractField
  currentComputeDAS28Activity;

  /**
   * Journal event properties relative to the specific JE implemented
   * @returns {Array<string>}
   */
  specificDas28Properties: string[] = [];

  /**
   * PROPERTIES
   */

  retrieveJournalEvent = JournalEventRetrievalStrategies.sourceJournalEvent;

  /**
   * The validation on the `das28` has been disabled here because it needs to be set
   * in the changeset before running the validations. It's too complicated to do while
   * the FORM is not a CRUD.
   */
  get validations(): ValidatorMap {
    const validationsObject = this.journalEventClass.validations(this.intl);
    const keys = Object.keys(validationsObject).filter(key => key != "das28");

    return keys.reduce(
      (result, key) => ({ ...result, [key]: validationsObject[key] }),
      {}
    );
  }

  /**
   * Those properties will be needed in every function that computes DAS28 score
   */
  sharedDas28Properties = ["nad", "nag", "patientFeeling"];

  /**
   * Those properties will be found in any JE that extends das28-computed-journal-event
   */
  standardJournalEventProperties = ["occurredAt", "das28"];

  /**
   * Those are the properties that will be used to compute the DAS28, within the
   * specific function
   */
  get computeDas28JournalEventProperties(): Array<string> {
    return [...this.sharedDas28Properties, ...this.specificDas28Properties];
  }

  get lastDAS28JE(): das28Je {
    return getAllDas28Jes(this.patient).sort(sortBy("occurredAt", "desc"))[0];
  }

  get das28ActivityTot(): string | undefined {
    return this.lastDAS28JE?.["das28Activity"];
  }

  get lastArticulationsInfos(): ArticulationsInfos {
    const lastJE = mostRecentJe(
      this.patient.lastPrArticulationCountedJournalEvent,
      this.lastDAS28JE
    );
    return {
      nad: Array.isArray(lastJE?.nad) ? lastJE?.nad.length : lastJE?.nad,
      nag: Array.isArray(lastJE?.nag) ? lastJE?.nag.length : lastJE?.nag,
      occurredAt: lastJE?.occurredAt
    };
  }

  get lastPatientFeelingInfos(): FeelingsInfo {
    const lastJE = mostRecentJe(
      this.patient.lastPrFeelingEvaPatientDeclaredJournalEvent,
      this.lastDAS28JE
    );

    return {
      patientFeeling: lastJE?.["feeling"] || lastJE?.["patientFeeling"],
      occurredAt: lastJE?.occurredAt
    };
  }

  get getNADJEDate(): string {
    if (
      this.lastArticulationsInfos.nad != undefined &&
      this.changeset.get("nad") == this.lastArticulationsInfos.nad
    ) {
      return dayjs(this.lastArticulationsInfos.occurredAt).format(
        "[Dernière mesure le] DD/MM/YYYY"
      );
    }
    return "";
  }

  get getNAGJEDate(): string {
    if (
      this.lastArticulationsInfos.nag != undefined &&
      this.changeset.get("nag") == this.lastArticulationsInfos?.nag
    ) {
      return dayjs(this.lastArticulationsInfos.occurredAt).format(
        "[Dernière mesure le] DD/MM/YYYY"
      );
    }
    return "";
  }

  get getPatientFeelingJEDate(): string {
    if (
      this.lastPatientFeelingInfos.patientFeeling != undefined &&
      this.changeset.get("patientFeeling") ==
        this.lastPatientFeelingInfos.patientFeeling
    ) {
      return dayjs(this.lastPatientFeelingInfos.occurredAt).format(
        "[Dernière mesure le] DD/MM/YYYY"
      );
    }
    return "";
  }

  /**
   * It calls the das28 computation function with the
   * final properties that we computed
   */
  get getDAS28(): number | string {
    const properties = this.computeDas28JournalEventProperties.reduce(
      (acc, cur) => {
        acc[cur] = parseFloat(this.changeset.get(cur));
        return acc;
      },
      {}
    );
    const das28 = this.currentComputeDas28Function(properties);
    if (!das28 || das28 == -Infinity) {
      return "—";
    }
    return das28;
  }

  get getDAS28Activity(): string {
    return this.currentComputeDAS28Activity(this.getDAS28);
  }

  retrieveModel() {
    const model = super.retrieveModel();
    if (this.isNewMode) {
      model.nad = this.lastArticulationsInfos?.nad;
      model.nag = this.lastArticulationsInfos?.nag;
      model.patientFeeling = this.lastPatientFeelingInfos?.patientFeeling;
      model.occurredAt = new Date();
    }
    return model;
  }

  get isDirty() {
    return true;
  }

  computePayload() {
    const payload = super.computePayload();

    payload.das28 = this.getDAS28;
    payload.occurredAt = this.changeset.get("occurredAt")
      ? this.changeset.get("occurredAt")
      : new Date();

    return payload;
  }
}
